import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as THREE from 'three';
import { updateRain } from '../utils/particles/handleRain';
import { updateClouds } from '../utils/particles/handleClouds';
import { updateFoliage } from '../utils/particles/handleFoliage';
import { updateWater } from '../utils/particles/handleWater';
import { updateStars } from '../utils/particles/handleStars';
import { updateSnow } from '../utils/particles/handleSnow';
import { updateTerrain } from '../utils/particles/handleTerrain';
import { updateFire } from '../utils/Elements';
import {
  startAnimation,
  pauseAnimation,
  stopAnimation,
  resetParticleSystem,
} from '../utils/particles/animationControls';
import { clearLastUpdatedParticle } from '../redux/Slice/particleSlice';
import store from '../redux/store';
import {
  applyParticleVisibility,
  isParticleConfigRenderable,
  isParticleSceneObject,
} from '../utils/ThreeJsUtils/assetVisibilityUtils';
import { restartParticlePlayback } from '../utils/particles/particleAnimationUtils';
import { refreshParticleSelectionOutline } from '../utils/ThreeJsUtils/selectionOutline';

const DEDICATED_UPDATER_TYPES = [
  'fire',
  'rain',
  'snow',
  'water',
  'stars',
  'foliage',
  'clouds',
  'terrain',
];

const useParticleSync = (scene, window_type) => {
  const particles = useSelector((state) => state.particles.particles);
  const lastUpdatedParticleId = useSelector(
    (state) => state.particles.lastUpdatedParticleId
  );

  const processParticleUpdate = (scene, particle) => {
    if (!particle?.id) return;
    const obj = scene.getObjectByName(particle.id);

    if (!obj) {
      return;
    }

    const isHidden = particle.visible === false;
    if (isHidden) {
      applyParticleVisibility(obj, false);
    }

    if (
      !isHidden &&
      (typeof particle.isPlaying !== 'undefined' ||
        typeof particle.isStopped !== 'undefined')
    ) {
      if (particle.isStopped) {
        stopAnimation(obj, particle.id);
        resetParticleSystem(obj, particle.id);
        obj.userData = {
          ...obj.userData,
          isStopped: true,
          isPlaying: false,
          isPaused: false,
        };
      } else if (particle.isPlaying) {
        obj.userData = {
          ...obj.userData,
          isStopped: false,
          isPlaying: true,
          isPaused: false,
        };
        resumeAnimationByType(obj, particle.id);
      } else {
        obj.userData = {
          ...obj.userData,
          isStopped: false,
          isPlaying: false,
          isPaused: true,
        };
        pauseAnimation(obj, particle.id);
      }
    }

    const hasDedicatedUpdater = DEDICATED_UPDATER_TYPES.includes(obj.objType);

    if (hasDedicatedUpdater) {
      if (obj.objType === 'rain') updateRain(scene, obj, particle);
      else if (obj.objType === 'snow') updateSnow(scene, obj, particle);
      else if (obj.objType === 'fire') updateFire(scene, obj, particle);
      else if (obj.objType === 'stars') updateStars(scene, obj, particle);
      else if (obj.objType === 'water') updateWater(scene, obj, particle);
      else if (obj.objType === 'foliage') updateFoliage(scene, obj, particle);
      else if (obj.objType === 'clouds') updateClouds(scene, obj, particle);
      else if (obj.objType === 'terrain') updateTerrain(scene, obj, particle);
    } else {
      processMaterialUpdates(obj, particle);
      processTransformUpdates(obj, particle);
      processTypeSpecificUpdates(obj, particle);
    }

    const editorVisible = particle.visible !== false;
    const lifeExpired = obj.userData?.lifeExpired === true;
    const configRenderable = isParticleSceneObject(obj)
      ? isParticleConfigRenderable(particle, obj.objType || particle.type)
      : true;

    if (!editorVisible) {
      applyParticleVisibility(obj, false);
    } else if (!lifeExpired) {
      applyParticleVisibility(obj, configRenderable);
    }
  };

  const processMaterialUpdates = (obj, particle) => {
    if (particle.color) {
      if (obj.material && obj.material instanceof THREE.PointsMaterial) {
        obj.material.color.set(particle.color);
        obj.material.needsUpdate = true;
      }
      if (obj.isGroup) {
        obj.traverse((child) => {
          if (child.material && child.material.color) {
            child.material.color.set(particle.color);
            child.material.needsUpdate = true;
          }
        });
      }
    }

    if (obj.material) {
      if (typeof particle.size !== 'undefined') {
        if (obj.material instanceof THREE.PointsMaterial) {
          obj.material.size = particle.size;
        }
        obj.material.needsUpdate = true;
      }

      if (typeof particle.opacity !== 'undefined') {
        obj.material.opacity = particle.opacity;
        obj.material.transparent = particle.opacity < 1;
        obj.material.needsUpdate = true;
      }

      if (typeof particle.sizeAttenuation !== 'undefined') {
        if (obj.material instanceof THREE.PointsMaterial) {
          obj.material.sizeAttenuation = particle.sizeAttenuation;
        }
        obj.material.needsUpdate = true;
      }

      if (particle.blending) {
        let blendingMode = THREE.NormalBlending;
        switch (particle.blending) {
          case 'AdditiveBlending':
            blendingMode = THREE.AdditiveBlending;
            break;
          case 'MultiplyBlending':
            blendingMode = THREE.MultiplyBlending;
            break;
          default:
            blendingMode = THREE.NormalBlending;
        }
        obj.material.blending = blendingMode;
        obj.material.needsUpdate = true;
      }
    }
  };

  const processTransformUpdates = (obj, particle) => {
    if (particle.position) {
      obj.position.set(
        particle.position.x || 0,
        particle.position.y || 0,
        particle.position.z || 0
      );
    }

    if (particle.rotation) {
      obj.rotation.set(
        particle.rotation.x || 0,
        particle.rotation.y || 0,
        particle.rotation.z || 0
      );
    }

    if (particle.scale) {
      obj.scale.set(
        particle.scale.x || 1,
        particle.scale.y || 1,
        particle.scale.z || 1
      );
    }
  };

  const processTypeSpecificUpdates = (obj, particle) => {
    if (typeof particle.intensity !== 'undefined') {
      if (obj.material && obj.material instanceof THREE.PointsMaterial) {
        let opacity = 0.5;
        if (particle.intensity === 'Low') opacity = 0.3;
        else if (particle.intensity === 'Medium') opacity = 0.6;
        else if (particle.intensity === 'High') opacity = 1.0;
        else if (!isNaN(Number(particle.intensity)))
          opacity = Math.max(
            0.1,
            Math.min(1, Number(particle.intensity) / 100)
          );
        obj.material.opacity = opacity;
        obj.material.needsUpdate = true;
      }
      if (obj.isGroup) {
        obj.traverse((child) => {
          if (child.material && typeof child.material.opacity === 'number') {
            let opacity = 0.5;
            if (particle.intensity === 'Low') opacity = 0.3;
            else if (particle.intensity === 'Medium') opacity = 0.6;
            else if (particle.intensity === 'High') opacity = 1.0;
            else if (!isNaN(Number(particle.intensity)))
              opacity = Math.max(
                0.1,
                Math.min(1, Number(particle.intensity) / 100)
              );
            child.material.opacity = opacity;
            child.material.needsUpdate = true;
          }
        });
      }
    }

    if (typeof particle.range !== 'undefined') {
      if (obj instanceof THREE.Points && obj.material && obj.material.size) {
        let size = 1;
        if (!isNaN(Number(particle.range)))
          size = Math.max(0.1, Math.min(1e9, Number(particle.range)));
        obj.material.size = size;
        obj.material.needsUpdate = true;
      }
      if (obj.isGroup) {
        let scale = 1;
        if (!isNaN(Number(particle.range)))
          scale = Math.max(0.1, Math.min(1e9, Number(particle.range)));
        // obj.scale.set(scale, scale, scale);
      }
    }

    if (particle.type === 'fire') {
      if (obj.isGroup) {
        obj.traverse((child) => {
          if (child.material && typeof particle.heatIntensity !== 'undefined') {
            if (child.material.opacity !== undefined) {
              child.material.opacity = particle.heatIntensity * 0.1;
              child.material.needsUpdate = true;
            }
          }
        });
      }
    }

    if (particle.type === 'stars') {
      if (obj.material && obj.material instanceof THREE.PointsMaterial) {
        if (typeof particle.twinkleIntensity !== 'undefined') {
          obj.userData.twinkleIntensity = particle.twinkleIntensity;
        }
        if (typeof particle.twinkleSpeed !== 'undefined') {
          obj.userData.twinkleSpeed = particle.twinkleSpeed;
        }
      }
    }

    if (particle.type === 'water') {
      if (obj.material) {
        if (typeof particle.waveHeight !== 'undefined') {
          obj.userData.waveHeight = particle.waveHeight;
        }
        if (typeof particle.waveSpeed !== 'undefined') {
          obj.userData.waveSpeed = particle.waveSpeed;
        }
        if (typeof particle.waveFrequency !== 'undefined') {
          obj.userData.waveFrequency = particle.waveFrequency;
        }
      }
    }

    if (particle.type === 'foliage') {
      if (obj.isGroup) {
        if (typeof particle.swayIntensity !== 'undefined') {
          obj.userData.swayIntensity = particle.swayIntensity;
        }
        if (typeof particle.swaySpeed !== 'undefined') {
          obj.userData.swaySpeed = particle.swaySpeed;
        }
        if (particle.pineColor) obj.userData.pineColor = particle.pineColor;
        if (particle.oakColor) obj.userData.oakColor = particle.oakColor;
        if (particle.birchColor) obj.userData.birchColor = particle.birchColor;
      }
    }

    if (particle.velocity) {
      obj.userData.velocity = particle.velocity;
    }
    if (particle.acceleration) {
      obj.userData.acceleration = particle.acceleration;
    }
    if (typeof particle.turbulence !== 'undefined') {
      obj.userData.turbulence = particle.turbulence;
    }
    if (particle.wind) {
      obj.userData.wind = particle.wind;
    }

    if (typeof particle.life !== 'undefined') {
      obj.userData.life = particle.life;
    }
    if (typeof particle.lifeVariation !== 'undefined') {
      obj.userData.lifeVariation = particle.lifeVariation;
    }

    if (typeof particle.colorVariation !== 'undefined') {
      obj.userData.colorVariation = particle.colorVariation;
    }
    if (typeof particle.colorTransition !== 'undefined') {
      obj.userData.colorTransition = particle.colorTransition;
    }

    if (typeof particle.sizeVariation !== 'undefined') {
      obj.userData.sizeVariation = particle.sizeVariation;
    }
    if (typeof particle.sizeTransition !== 'undefined') {
      obj.userData.sizeTransition = particle.sizeTransition;
    }
  };


  const initialSyncDoneRef = useRef(false);

  useEffect(() => {
    initialSyncDoneRef.current = false;
  }, [scene]);

  useEffect(() => {
    if (!scene || !Array.isArray(particles)) return;

    if (lastUpdatedParticleId) {
      const particle = particles.find((p) => p.id === lastUpdatedParticleId);
      if (particle) {
        processParticleUpdate(scene, particle);
        // Rebuild outline proxies after count/size/shape updates.
        refreshParticleSelectionOutline();
      }
      store.dispatch(clearLastUpdatedParticle());
      return;
    }

    // One-time sync when scene becomes available with existing Redux particles.
    if (!initialSyncDoneRef.current && particles.length > 0) {
      initialSyncDoneRef.current = true;
      particles.forEach((particle) => {
        processParticleUpdate(scene, particle);
      });
    }
  }, [scene, particles, lastUpdatedParticleId, window_type]);

  const resumeAnimationByType = (obj, particleId) => {
    if (!obj || !particleId) return;

    const animateFunction = obj.userData.animateFunction;
    if (animateFunction) {
      startAnimation(obj, animateFunction, particleId);
    } else {
      console.warn(`No animation function found for particle: ${particleId}`);
    }
  };
};

export default useParticleSync;
